import { useRouter } from 'vue-router/composables';
import { useAssistantStore } from '@/composition/assistant/use-assistant-store';
import { useAuthStore } from '@/composition/use-auth';
import { openExternal } from '@/lib/open-external';
import { useRestaurantStore } from '@/stores/restaurant.store';
import type { IHostAdapter } from './gpthelp-host-contract';
import { buildAdminHostAdapter } from './assistant/assistant-host-adapter';
import { ensureAssistantConfig } from './assistant/assistant-config';
import { hookAssistantIdentityReset } from './assistant/assistant-identity-reset';
import { warmGpthelpBusinessDay } from './cartadigital-admin-host-context';
import { useAdminHostToolDeps } from './gpthelp-host-deps';

/**
 * The host adapter v2 the admin gives `<gpt-help>` (the «Asistente» panel
 * and the bubble; plan §9.5). Call it in a component's setup (it reads the
 * stores and the router).
 */
export function createAdminHostAdapter(): IHostAdapter {
  const deps = useAdminHostToolDeps();
  const router = useRouter();
  const { authStore } = deps;
  hookAssistantIdentityReset(() => authStore.user?.id ?? null);

  return buildAdminHostAdapter(deps, {
    push(location) {
      // A duplicated or guarded navigation is not an error for the chat.
      router.push(location).catch(() => undefined);
    },
    openExternal,
    isOnline: () => navigator.onLine,
    reload: () => window.location.reload(),
  });
}

/**
 * Warms what the widget reads right after a restaurant/branch switch: the
 * business day, so `getContext()` on send never waits for the network, and
 * (for the «Asistente» panel) what the assistant may change in this store
 * and branch (plan §9.1 step 4), so the first send already offers it.
 */
export function warmAdminHostContext(): void {
  const restaurantStore = useRestaurantStore();
  const authStore = useAuthStore();
  const assistantStore = useAssistantStore();
  void warmGpthelpBusinessDay({ restaurantStore, authStore }).catch(
    () => undefined
  );
  // The bubble (stores outside the rollout) never asks for it here.
  if (assistantStore.enabled)
    void ensureAssistantConfig({ restaurantStore, authStore, assistantStore });
}
